// Appearance (vault/Apps/Forge/Pages/Appearance.md) — the ORG's theme
// defaults, adopted from DocujointOrg.dc.html (imported 2026-08-06).
//
// f1  Three choices, each a NAME from a closed vocabulary the control plane
//     shares (platform/src/appearance.ts): base theme, accent, radius. The
//     CSS a name resolves to is src/appearance-tokens.ts — the one table this
//     page's preview and the applied chrome both read, which is what makes
//     the preview honest.
// f2  LIVE PREVIEW, scoped: the draft's vars are painted onto the preview
//     panel alone. The workspace itself changes only when the save LANDS —
//     an unsaved draft restyling the whole app would show every member a
//     theme nobody chose yet.
// f3  Save is GOVERN's (the Worker refuses others naming the guard); the
//     buttons are drawn for the governing role alone — offered only what the
//     guard would allow, refused server-side regardless.
// f4  Reset saves the DEFAULTS — an act, not an undo: the row records who
//     reset and when, exactly like any other save.
//
// GAPS, on the record (the design draws more than exists): the organization
// LOGO dropzone and the per-surface Dashboards/Reports tabs are not built —
// a dropzone that uploads nowhere and tabs onto empty panes are hollow
// doors, so they arrive with their machinery, not before.
import { useEffect, useMemo, useState } from 'react';

import { Badge } from '@astryxdesign/core/Badge';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { Selector } from '@astryxdesign/core/Selector';
import { Stack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';

import type { AppearanceClient, OrgAppearance } from '../api/appearance';
import {
  ACCENT_OPTIONS,
  BASE_OPTIONS,
  RADIUS_OPTIONS,
  appearanceVars,
} from '../appearance-tokens';
import { Eyebrow } from '../components/Eyebrow';
import type { Role } from '../shell/AppShell';

import './appearance.css';

export interface AppearancePageProps {
  org: string;
  role: Role | null;
  client: AppearanceClient;
  /** the applied org appearance changed — App repaints the chrome with it */
  onSaved?: (appearance: OrgAppearance) => void;
}

const DEFAULT_CHOICE = { base: 'astryx', accent: 'blue', radius: 'default' };

export function AppearancePage({ org, role, client, onSaved }: AppearancePageProps) {
  const [saved, setSaved] = useState<OrgAppearance | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [draft, setDraft] = useState(DEFAULT_CHOICE);
  const [saving, setSaving] = useState(false);
  const [landed, setLanded] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setSaved(null);
    setRefusal(null);
    setLanded(null);
    void client
      .load(org)
      .then((appearance) => {
        if (!live) return;
        setSaved(appearance);
        setDraft({ base: appearance.base, accent: appearance.accent, radius: appearance.radius });
      })
      .catch((error: Error) => {
        if (live) setRefusal(error.message);
      });
    return () => {
      live = false;
    };
  }, [org, client]);

  // f2 — the draft's vars, painted on the PREVIEW PANEL alone.
  const previewVars = useMemo(() => appearanceVars(draft), [draft]);

  const governs = role === 'admin';
  const dirty =
    saved !== null &&
    (draft.base !== saved.base || draft.accent !== saved.accent || draft.radius !== saved.radius);

  const land = async (choice: typeof DEFAULT_CHOICE) => {
    setSaving(true);
    setRefusal(null);
    setLanded(null);
    try {
      const appearance = await client.save(org, choice);
      setSaved(appearance);
      setDraft({ base: appearance.base, accent: appearance.accent, radius: appearance.radius });
      setLanded('Saved — every member sees it on their next paint.');
      onSaved?.(appearance);
    } catch (error) {
      setRefusal((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-page="appearance" className="dj-appearance">
      <Stack direction="vertical" gap={2}>
        <Heading level={1} type="display-3">
          Appearance
        </Heading>
        <Text type="body" color="secondary">
          Organization defaults for every workspace surface. Dashboards carry their own theme in
          dashboard.yaml, and it wins on their pages.
        </Text>
      </Stack>

      {refusal ? (
        <Text type="body" color="secondary" data-testid="appearance-refusal">
          {refusal}
        </Text>
      ) : saved === null ? (
        <Text type="supporting" color="secondary">
          Reading the organization&rsquo;s appearance…
        </Text>
      ) : (
        <div className="dj-appearance-columns">
          <Stack direction="vertical" gap={4} className="dj-appearance-form">
            <Selector
              label="Base theme"
              size="sm"
              width="100%"
              options={[...BASE_OPTIONS]}
              value={draft.base}
              onChange={(base: string) => setDraft((d) => ({ ...d, base }))}
              isDisabled={!governs}
            />
            <Stack direction="vertical" gap={1}>
              <Selector
                label="Accent"
                size="sm"
                width="100%"
                options={[...ACCENT_OPTIONS]}
                value={draft.accent}
                onChange={(accent: string) => setDraft((d) => ({ ...d, accent }))}
                isDisabled={!governs}
              />
              <Text type="supporting" color="secondary">
                Applied to links, selection and chart primaries.
              </Text>
            </Stack>
            <Selector
              label="Radius scale"
              size="sm"
              width="100%"
              options={[...RADIUS_OPTIONS]}
              value={draft.radius}
              onChange={(radius: string) => setDraft((d) => ({ ...d, radius }))}
              isDisabled={!governs}
            />

            <Text type="supporting" color="secondary">
              Knowledge bases and dashboards can override this theme individually.
            </Text>

            {/* f3 — the write is offered to govern alone; everyone else READS
                the org's appearance and the controls above say why they are
                still. */}
            {governs ? (
              <Stack direction="horizontal" gap={2} vAlign="center">
                <Button
                  type="button"
                  variant="primary"
                  label="Save appearance"
                  isDisabled={saving || !dirty}
                  onClick={() => void land(draft)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  label="Reset"
                  isDisabled={saving}
                  onClick={() => void land(DEFAULT_CHOICE)}
                />
                {landed ? (
                  <Text type="supporting" color="secondary">
                    {landed}
                  </Text>
                ) : null}
              </Stack>
            ) : (
              <Text type="supporting" color="secondary">
                Changing the organization&rsquo;s appearance is an admin&rsquo;s act — these are the
                defaults your admin chose.
              </Text>
            )}

            {saved.updatedAt ? (
              <Text type="code" color="secondary">
                last saved by {saved.updatedBy} · {saved.updatedAt.slice(0, 10)}
              </Text>
            ) : null}
          </Stack>

          {/* f2 — the preview: a miniature of the surfaces the theme paints,
              under the DRAFT's vars, scoped to this panel. */}
          <div className="dj-appearance-preview" style={previewVars} data-testid="appearance-preview">
            <Card padding={4} elevation="none">
              <Stack direction="vertical" gap={3}>
                <Stack direction="horizontal" gap={2} vAlign="center">
                  <Eyebrow>Live preview</Eyebrow>
                  <span className="dj-appearance-stamp">
                    {draft.base} · {draft.accent} · {draft.radius}
                  </span>
                </Stack>
                <Stack direction="horizontal" gap={2} vAlign="center">
                  <span className="dj-appearance-swatch" aria-hidden="true" />
                  <Text type="label" weight="semibold">
                    docujoint-cloud
                  </Text>
                  <Badge label="green" variant="success" />
                </Stack>
                <div className="dj-appearance-stats">
                  <Card padding={3} elevation="none">
                    <Stack direction="vertical" gap={1}>
                      <Text type="label" weight="semibold">
                        104
                      </Text>
                      <Eyebrow>Records</Eyebrow>
                    </Stack>
                  </Card>
                  <Card padding={3} elevation="none">
                    <Stack direction="vertical" gap={1}>
                      <Text type="label" weight="semibold">
                        9
                      </Text>
                      <Eyebrow>Open questions</Eyebrow>
                    </Stack>
                  </Card>
                </div>
                <Stack direction="vertical" gap={2}>
                  <a className="dj-appearance-link" href="#preview" onClick={(e) => e.preventDefault()}>
                    An accent-colored link
                  </a>
                  <Button type="button" variant="primary" size="sm" label="A primary action" />
                </Stack>
              </Stack>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
